import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification, useTranslate } from "@refinedev/core";

import { usePaginatedTable, useQueryParams } from "@shared/hooks";
import { IntegrationEndpoint } from "@modules/erpIntegrations/types";
import { EndpointFormValues } from "@modules/erpIntegrations/validations";
import { getErpErrorMessage } from "@modules/erpIntegrations/utils";
import {
  countPlanMapOptionsByEndpoint,
  listIntegrationEndpointsQueryKey,
  listIntegrationHistoryQueryKey,
  useListIntegrationsQuery,
} from "@modules/erpIntegrations/services/queries";
import {
  useCreateEndpointMutation,
  useDeleteEndpointMutation,
  useEditEndpointMutation,
} from "@modules/erpIntegrations/services/mutations";

export function useEndpointsTab() {
  const translate = useTranslate();
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const { getQueryParams } = useQueryParams();

  const params = getQueryParams();
  const filters = {
    ...(params.endpoints_integration ? { integration: params.endpoints_integration } : {}),
    ...(params.endpoints_search ? { search: params.endpoints_search } : {}),
    ...(params.endpoints_method ? { method: params.endpoints_method } : {}),
    ...(params.endpoints_active ? { active: params.endpoints_active } : {}),
  };

  const { rows, loading, refetch, ...tableProps } = usePaginatedTable<IntegrationEndpoint>({
    resource: "v1/integration_endpoint_admin",
    filters,
    sorters: { ordering: "-updated_at" },
  });

  const { data: integrations = [] } = useListIntegrationsQuery({ perPage: 100 });

  const [dialogEndpoint, setDialogEndpoint] = useState<IntegrationEndpoint | "new" | null>(null);
  const [historyEndpoint, setHistoryEndpoint] = useState<IntegrationEndpoint | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: listIntegrationEndpointsQueryKey,
      refetchType: "all",
    });
    queryClient.invalidateQueries({
      queryKey: listIntegrationHistoryQueryKey,
      refetchType: "all",
    });
    refetch();
  };

  const createMutation = useCreateEndpointMutation({
    onSuccess: () => {
      invalidate();
      open?.({ type: "success", message: translate("erp_integrations.endpoints.create_success") });
      setDialogEndpoint(null);
    },
  });

  const editMutation = useEditEndpointMutation({
    onSuccess: (_endpoint, variables) => {
      invalidate();
      open?.({ type: "success", message: translate("erp_integrations.endpoints.edit_success") });
      if (variables.method === "put") setDialogEndpoint(null);
    },
  });

  const deleteMutation = useDeleteEndpointMutation({
    onSuccess: () => {
      invalidate();
      open?.({ type: "success", message: translate("erp_integrations.endpoints.delete_success") });
    },
  });

  const handleDelete = async (endpoint: IntegrationEndpoint) => {
    try {
      const count = await countPlanMapOptionsByEndpoint(endpoint.id);
      if (count > 0) {
        open?.({
          type: "error",
          message: translate("erp_integrations.endpoints.delete_blocked"),
        });
        return;
      }
    } catch (error) {
      open?.({ type: "error", message: getErpErrorMessage(error) });
      return;
    }

    if (!window.confirm(translate("erp_integrations.endpoints.confirm_delete"))) return;
    deleteMutation.mutate(endpoint.id);
  };

  const handleToggleActive = (endpoint: IntegrationEndpoint) => {
    editMutation.mutate({
      id: endpoint.id,
      method: "patch",
      data: { active: !endpoint.active },
    });
  };

  const handleSubmit = (data: EndpointFormValues, existing: IntegrationEndpoint | null) => {
    if (existing) {
      editMutation.mutate({ id: existing.id, method: "put", data });
      return;
    }
    createMutation.mutate(data);
  };

  return {
    rows,
    loading,
    tableProps,
    integrations,
    translate,
    dialogEndpoint,
    openCreateDialog: () => setDialogEndpoint("new"),
    openEditDialog: (endpoint: IntegrationEndpoint) => setDialogEndpoint(endpoint),
    closeDialog: () => setDialogEndpoint(null),
    historyEndpoint,
    openHistory: (endpoint: IntegrationEndpoint) => setHistoryEndpoint(endpoint),
    closeHistory: () => setHistoryEndpoint(null),
    handleDelete,
    handleToggleActive,
    handleSubmit,
    isSaving: createMutation.isPending || editMutation.isPending,
    isMutating: createMutation.isPending || editMutation.isPending || deleteMutation.isPending,
    defaultIntegrationId: params.endpoints_integration
      ? Number(params.endpoints_integration)
      : undefined,
  };
}
